import dayjs, { type Dayjs } from 'dayjs';

/**
 * The windows the date filter offers: the dashboard's own, so "last week" means the same
 * eight days on both pages, and today beside them. Each one ends today.
 */
export const datePresets = [
  { id: 'today', days: 0, label: 'Inspections_Filter_Today' },
  { id: '7d', days: 7, label: 'Statistic_LastWeek' },
  { id: '30d', days: 30, label: 'Statistic_LastMonth' },
  { id: '90d', days: 90, label: 'Statistic_LastQuarter' },
  { id: '365d', days: 365, label: 'Statistic_LastYear' },
] as const;

export type DatePresetId = typeof datePresets[number]['id'];

/**
 * A preset is kept as a preset rather than as the two days it stood for when it was picked,
 * so a link to "last week" still means last week when it is opened next month. A custom
 * range is calendar days in the reader's time zone, both inclusive.
 */
export type DateFilter =
  | { kind: 'preset'; preset: DatePresetId }
  | { kind: 'custom'; from: string; to: string };

export interface InspectionFilters {
  search: string;
  projectIds: string[];
  userIds: string[];
  teamIds: string[];
  date: DateFilter | null;
}

export const noFilters: InspectionFilters = { search: '', projectIds: [], userIds: [], teamIds: [], date: null };

/** What the list sends; the endpoint's own argument, page and size beside it. */
export interface InspectionFilterArgs {
  search?: string;
  projectIds?: string[];
  userIds?: string[];
  teamIds?: string[];
  from?: string;
  to?: string;
}

const PARAM = {
  search: 'q',
  projectIds: 'projects',
  userIds: 'users',
  teamIds: 'teams',
  range: 'range',
  from: 'from',
  to: 'to',
  page: 'page',
} as const;

export const DAY_FORMAT = 'YYYY-MM-DD';

/** A day written the one way the URL holds it; `2026-02-31` parses, to March, so it is checked by writing it back. */
const isDay = (value: string | null): value is string =>
  Boolean(value) && dayjs(value).isValid() && dayjs(value).format(DAY_FORMAT) === value;

/** What the endpoint takes, and refuses past: ids in one list, and the length of the search. */
export const MAX_LISTED_IDS = 100;
export const MAX_SEARCH_LENGTH = 200;

/** A user's or a team's id is a MongoDB id; a project's is the app's own, held only to its size. */
const isObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
const isProjectId = (id: string) => id.length <= 128;

/**
 * An id list as the endpoint would take it. An id it would refuse is left out: it names no
 * inspection, so the list is the same without it. A list longer than it takes is no filter.
 */
const readIds = (params: URLSearchParams, key: string, isId: (id: string) => boolean): string[] => {
  const ids = [...new Set((params.get(key) ?? '').split(',').map(id => id.trim()).filter(Boolean))].filter(isId);

  return ids.length > MAX_LISTED_IDS ? [] : ids;
};

/**
 * The filters a URL carries. Anything it cannot read is no filter rather than an error: a
 * link edited by hand opens the list, only less narrowed than its author meant.
 */
export const readFilters = (params: URLSearchParams): InspectionFilters => {
  const range = params.get(PARAM.range);
  const from = params.get(PARAM.from);
  const to = params.get(PARAM.to);

  const preset = datePresets.find(candidate => candidate.id === range);

  let date: DateFilter | null = null;

  if (preset) {
    date = { kind: 'preset', preset: preset.id };
  } else if (isDay(from) && isDay(to)) {
    // Days of one format compare as strings; a range written backwards is read forwards.
    date = from <= to ? { kind: 'custom', from, to } : { kind: 'custom', from: to, to: from };
  }

  return {
    search: (params.get(PARAM.search) ?? '').slice(0, MAX_SEARCH_LENGTH),
    projectIds: readIds(params, PARAM.projectIds, isProjectId),
    userIds: readIds(params, PARAM.userIds, isObjectId),
    teamIds: readIds(params, PARAM.teamIds, isObjectId),
    date,
  };
};

export const readPage = (params: URLSearchParams): number => Math.max(1, parseInt(params.get(PARAM.page) ?? '') || 1);

/** The URL of the filters: only what is set, so the list with nothing set is the bare path. */
export const writeFilters = (filters: InspectionFilters, page = 1): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.search) params.set(PARAM.search, filters.search);
  if (filters.projectIds.length) params.set(PARAM.projectIds, filters.projectIds.join(','));
  if (filters.userIds.length) params.set(PARAM.userIds, filters.userIds.join(','));
  if (filters.teamIds.length) params.set(PARAM.teamIds, filters.teamIds.join(','));

  if (filters.date?.kind === 'preset') params.set(PARAM.range, filters.date.preset);

  if (filters.date?.kind === 'custom') {
    params.set(PARAM.from, filters.date.from);
    params.set(PARAM.to, filters.date.to);
  }

  if (page > 1) params.set(PARAM.page, String(page));

  return params;
};

/** The first and the last day a date filter covers, as local days. */
export const dateRangeOf = (date: DateFilter, today: Dayjs = dayjs()): { from: Dayjs; to: Dayjs } => {
  if (date.kind === 'custom') return { from: dayjs(date.from), to: dayjs(date.to) };

  const { days } = datePresets.find(preset => preset.id === date.preset)!;

  return { from: today.subtract(days, 'day').startOf('day'), to: today.startOf('day') };
};

/**
 * What the endpoint is asked. The days become instants here, where the reader's time zone
 * is known: the start of the first day and the end of the last. Both are whole days, so a
 * preset asks the same thing all day long and lands on one cache entry. `today` is the day a
 * preset ends on, handed in by a page that moves it at midnight.
 *
 * A NUL is taken out of the search, which the endpoint refuses: a link edited by hand opens
 * the list.
 */
export const toFilterArgs = (filters: InspectionFilters, today: Dayjs = dayjs()): InspectionFilterArgs => {
  const search = filters.search.split('\u0000').join('').trim();
  const range = filters.date && dateRangeOf(filters.date, today);

  return {
    search: search || undefined,
    projectIds: filters.projectIds.length ? filters.projectIds : undefined,
    userIds: filters.userIds.length ? filters.userIds : undefined,
    teamIds: filters.teamIds.length ? filters.teamIds : undefined,
    from: range?.from.startOf('day').toISOString(),
    to: range?.to.endOf('day').toISOString(),
  };
};

export const hasFilters = (filters: InspectionFilters): boolean =>
  Boolean(filters.search.trim()) || filters.projectIds.length > 0 || filters.userIds.length > 0
  || filters.teamIds.length > 0 || filters.date !== null;
